/**
 * Native push notifications bridge for Eventom (Capacitor + FCM).
 *
 * On the web this file is a no-op (returns null).  When the React app runs
 * inside the Capacitor Android/iOS shell, it registers with APNs / FCM and
 * saves the device token to `/api/notifications/push-token` so backend can
 * fan out event reminders, ClubVIP match notifications and ticket updates.
 *
 * Call `registerNativePush()` once after successful login.
 */
import { api } from "@/lib/api";

let registered = false;

export async function registerNativePush() {
  if (registered) return true;
  if (typeof window === "undefined") return null;
  const isNative = !!window.Capacitor?.isNativePlatform?.();
  if (!isNative) return null;

  let PushNotifications;
  try {
    ({ PushNotifications } = await import("@capacitor/push-notifications"));
  } catch (e) {
    console.warn("push notifications module not installed", e);
    return null;
  }

  const perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    const req = await PushNotifications.requestPermissions();
    if (req.receive !== "granted") return false;
  } else if (perm.receive !== "granted") {
    return false;
  }
  await PushNotifications.register();

  PushNotifications.addListener("registration", async (token) => {
    try {
      await api.post("/notifications/push-token", {
        token: token.value,
        platform: window.Capacitor?.getPlatform?.() || "unknown",
      });
    } catch (err) { console.warn("push token save failed", err); }
  });
  PushNotifications.addListener("registrationError", (err) => console.warn("push registration error", err));
  PushNotifications.addListener("pushNotificationReceived", (n) => {
    try {
      const bid = n?.data?.broadcast_id;
      if (bid) api.post("/notifications/push-track", { broadcast_id: bid, kind: "open" }).catch(() => {});
      window.dispatchEvent(new CustomEvent("eventom:push", { detail: n }));
    } catch {}
  });
  PushNotifications.addListener("pushNotificationActionPerformed", (a) => {
    const bid = a?.notification?.data?.broadcast_id;
    if (bid) api.post("/notifications/push-track", { broadcast_id: bid, kind: "tap" }).catch(() => {});
    const link = a.notification?.data?.link;
    if (link && typeof window !== "undefined") window.location.href = link;
  });

  registered = true;
  return true;
}
